import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAccess from '@/hooks/useAccess';
import { AccessInvitationRead, AccessRole, ElementPosition } from '@/types';
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import UserCard from '@/components/user/UserCard';
import TableList from '@/components/list/tableList/tableList';
import TableListRow from '@/components/list/tableList/tableListRow';
import TableListCell from '@/components/list/tableList/tableListCell';
import Avatar from '@/components/user/Avatar';
import SearchResultHolder from './SearchResultHolder';

interface UserSelectorProps {
	storageId: string;
	value: string | null;
	onChange: (userId: string) => void;
	label?: string;
	required?: boolean;
}

const allowedRoles = [AccessRole.OWNER, AccessRole.EDITOR, AccessRole.WORKER];

const invitationUserId = (invitation: AccessInvitationRead) =>
	invitation.userId || invitation.user?.id || '';

const UserSelector: React.FC<UserSelectorProps> = ({ storageId, value, onChange, label, required }) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');
	const { storageInvitations } = useAccess({ storageId });
	const [inFocus, setInFocus] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const UserSelectorWrapperRef = useRef<HTMLDivElement>(null);
	const UserSelectorResultsRef = useRef<HTMLDivElement>(null);
	const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);

	const users = useMemo(() =>
		storageInvitations.filter((inv) => {
			if (!allowedRoles.includes(inv.role) || inv.isDeleted) return false;
			if (inv.role === AccessRole.OWNER) return Boolean(invitationUserId(inv));
			return inv.status === 'accepted' && Boolean(invitationUserId(inv));
		}),
		[storageInvitations]
	);

	const filteredUsers = useMemo(() =>
		users.filter((u) => {
			const name = u.user?.name || u.invitation?.email || '';
			return name.toLowerCase().includes(search.toLowerCase());
		}),
		[users, search]
	);

	const selectedUser = value
		? users.find((u) => invitationUserId(u) === value)
		: undefined;

	const handleOnClick = () => {
		setInFocus(true);
		if (UserSelectorWrapperRef.current) {
			const rect = UserSelectorWrapperRef.current.getBoundingClientRect();
			setInputLocation({
				top: rect.bottom,
				left: rect.left,
				width: rect.width
			});
		}
	};

	const handleOnBlur = () => {
		setInFocus(false);
	};

	const handleSearchIconClick = () => {
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
	};

	const handleSelectUser = (userId: string) => {
		if (!userId) return;
		onChange(userId);
		setSearch('');
		setInFocus(false);
	};

	useEffect(() => {
		if (!inFocus) return;
		const updateRect = () => {
			if (UserSelectorWrapperRef.current) {
				const rect = UserSelectorWrapperRef.current.getBoundingClientRect();
				setInputLocation({
					top: rect.bottom,
					left: rect.left,
					width: rect.width
				});
			}
		};
		window.addEventListener('resize', updateRect);
		updateRect();
		return () => window.removeEventListener('resize', updateRect);
	}, [inFocus]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				UserSelectorWrapperRef.current && !UserSelectorWrapperRef.current.contains(target) &&
				UserSelectorResultsRef.current && !UserSelectorResultsRef.current.contains(target)
			) {
				handleOnBlur();
			}
		};
		if (inFocus) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [inFocus]);

	const selectedUserPreview = value ? (
		<div className="flex items-center gap-2 mt-2">
			<UserCard
				userId={value}
				avatarUrl={selectedUser?.user?.other?.avatarUrl}
				name={selectedUser?.user?.name || selectedUser?.invitation?.email}
				size="small"
				onClose={() => onChange('')}
			/>
		</div>
	) : null;

	const userSearch = value ? null : (
		<div ref={UserSelectorWrapperRef} className="relative">
			<div className="relative w-full">
				<SearchIcon
					className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer fill-gray-500"
					onClick={handleSearchIconClick}
				/>
				<input
					type="text"
					placeholder={inFocus ? t('palceholderSelectUser') : ''}
					value={inFocus ? search : ''}
					onChange={(e) => setSearch(e.target.value)}
					className={`
                        rounded-md
                        w-full
                        focus:outline-none
                        px-10
                        ${inFocus ? `border-gray-500 border-b-white rounded-b-none` : ``}
                    `} 
					ref={searchInputRef}
					onFocus={handleOnClick}
					data-ignore="true"
					disabled={!storageId}
				/>
				{inFocus && search.length > 0 && (
					<CloseIcon
						className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer fill-black"
						onClick={() => setSearch('')}
					/>
				)}
			</div>
		</div>
	);

	const results = value ? null : inFocus && inputLocation && (
		<SearchResultHolder
			inputRef={inputLocation}
			resultsRef={UserSelectorResultsRef}
		>
			{filteredUsers.length > 0 ? (
				<TableList className='border-none rounded-t-none'>
					<TableListRow isHeader hoverEffect={false}>
						<TableListCell></TableListCell>
						<TableListCell>{t('titleName')}</TableListCell>
						<TableListCell>{t('titleEmail')}</TableListCell>
					</TableListRow>
					{filteredUsers.map((user) => {
						const userId = invitationUserId(user);
						return (
							<TableListRow
								key={userId || user.invitation?.email}
								onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
								onClick={() => handleSelectUser(userId)}
								className={`cursor-pointer ${value === userId ? 'bg-blue-100' : ''}`}
							>
								<TableListCell>
									<Avatar
										avatarUrl={user.user?.other?.avatarUrl}
										email={user.invitation?.email}
										name={user.user?.name}
										hasAvatar={user.user?.other?.hasAvatar}
										size="sm"
									/>
								</TableListCell>
								<TableListCell>{user.user?.name || user.invitation?.email}</TableListCell>
								<TableListCell>{user.invitation?.email}</TableListCell>
							</TableListRow>
						);
					})}
				</TableList>
			) : (
				<div className="text-gray-500 p-3">{t('noUsersFound')}</div>
			)}
		</SearchResultHolder>
	);

	return (
		<div className="input-holder-wide relative">
			{label && <label>{label}{required ? '*' : ''}</label>}
			{userSearch}
			{results}
			{selectedUserPreview}
		</div>
	);
};

export default UserSelector;
